/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ReactNode, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { Sidebar } from '@futrli/ui-components';
import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';
import Form from 'carbon-react/lib/components/form';

import { toastError, UILoaderSpinner } from '@futrli/ui-components';
import { Integration, BillingModel } from '@futrli-sdk/app-store';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import {
  ACCOUNTING_TAG,
  createDatasourceByIntegration,
  getOrganisationsSettingsUrl,
  IntegrationStartCarbon
} from '@futrli/shared-services';

import * as styles from './add-org-drawer.scss';
import { filterIntregationsByTag } from '../../../datasources/utils/integration-utils';
import { StoreState } from '../../../core/types';
import { activateProduct, createOrg, createProviderConnection } from '../../../core/api';
import { getCallbackUrl } from '../../../datasources/utils/routes-utils';
import { trackEvent } from '../../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../../predict/src/core/constants';
import { DemoOrganisations } from './demo-organisations/demo-organisations';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  integrations: Integration[];
  estimatesText: ReactNode | null;
  productId: string;
  workspaceId: string;
  openExcelUploadModal: () => void;
}

export const AddOrgDrawer = ({
  isOpen,
  onClose,
  isLoading,
  integrations,
  productId,
  workspaceId,
  estimatesText,
  openExcelUploadModal
}: Props) => {
  const intl = useIntl();
  const [isError, setIsError] = useState(false);
  const [activeIntegrationId, setActiveIntegrationId] = useState<string>();

  const currentUserId = useSelector((state: StoreState) => {
    return get(state, 'authReducers.authenticatedUser.username') || undefined;
  });

  const tempSupportedProviders = ['sage', 'xero', 'qbo'];

  const integrationsFilterByTag = filterIntregationsByTag(integrations, ACCOUNTING_TAG);
  const filteredIntegrations = integrationsFilterByTag.filter(i => tempSupportedProviders.indexOf(i.slug) !== -1);
  const excelIntegration = integrations.find(i => i.slug === 'excel');

  const demoIntegrations = integrations.filter(i => i.slug === 'demo-au' || i.slug === 'demo-eu');

  const handleConnectClick = useCallback(
    async (integration: Integration) => {
      if (!productId) {
        toastError(
          <FormattedMessage
            id="organisations.issue_creating_new_org"
            defaultMessage="There has been an issue creating a new organisation. Please try again and contact support if the problem persists."
          />
        );
        return;
      }
      if (integration.slug === 'excel') {
        openExcelUploadModal();
        return;
      }
      try {
        const isDemoOrg = integration.slug === 'demo-au' || integration.slug === 'demo-eu';
        setActiveIntegrationId(integration.id);
        setIsError(false);
        const ds = createDatasourceByIntegration(integration.id, integrationsFilterByTag);
        const tmpOrg = { name: 'Organisation', datasource: ds };
        const org = await createOrg(workspaceId, tmpOrg);

        await activateProduct(workspaceId, org.id, productId, BillingModel.free);

        const response = await createProviderConnection(workspaceId, org.id, {
          workspace_id: workspaceId,
          organisation_id: org.id,
          datasource_id: org.datasource.id,
          integration:
            integration.slug === 'demo-au' ? 'demo_au' : integration.slug === 'demo-eu' ? 'demo_eu' : integration.slug,
          user_id: currentUserId || '',
          provider: isDemoOrg ? 'demo' : integration.slug === 'xero' ? 'xero' : 'ledgerflow',
          success_url: getCallbackUrl(getOrganisationsSettingsUrl(workspaceId))
        });

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          switch (integration.slug) {
            case 'demo-au':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_AU_ORGANISATION);
            case 'demo-eu':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_UK_ORGANISATION);
            case 'qbo':
              trackEvent(TRACKING_EVENTS.ADDED_QBO_ORGANISATION);
            case 'sage':
              trackEvent(TRACKING_EVENTS.ADDED_SAGE_ORGANISATION);
            default:
              trackEvent(TRACKING_EVENTS.ADDED_XERO_ORGANISATION);
          }
          window.location.href = response.initiation_url;
        }
      } catch (e) {
        setIsError(true);
        setActiveIntegrationId(undefined);
      }
    },
    [integrationsFilterByTag, workspaceId, productId, currentUserId, openExcelUploadModal]
  );

  return (
    <Sidebar
      open={isOpen}
      data-testid="snapshot-drawer"
      padding={0}
      aria-label={intl.formatMessage({
        id: 'organisations.add_an_organisation_sidebar.aria_label',
        defaultMessage: 'Add an organisation sidebar'
      })}
      onCancel={onClose}
      header={
        <Typography variant="h2" fontWeight="700">
          <FormattedMessage id="organisations.add_an_organisation" defaultMessage="Add an organisation" />
        </Typography>
      }
    >
      <Form
        rightSideButtons={
          <>
            <Button buttonType="secondary" onClick={onClose}>
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Button>
          </>
        }
        stickyFooter
        buttonAlignment="right"
        footerPadding={{
          p: 2
        }}
        onSubmit={e => e.preventDefault()}
      >
        {isLoading ? (
          <div style={{ marginTop: 64, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <UILoaderSpinner />
          </div>
        ) : (
          <div>
            <div className={styles.content}>
              <Typography fontWeight="500" mb="4px" fontSize="16px">
                <FormattedMessage
                  id="organisations.choose_acc_software"
                  defaultMessage="Choose your accounting software below"
                />
              </Typography>

              <Typography fontSize="14px" fontWeight="400">
                {estimatesText}
              </Typography>

              <div>
                {filteredIntegrations.reverse().map(integration => (
                  <IntegrationStartCarbon
                    activeIntegrationId={activeIntegrationId}
                    error={isError}
                    integration={integration}
                    key={integration.id}
                    onClick={handleConnectClick}
                  />
                ))}
                {excelIntegration && (
                  <IntegrationStartCarbon
                    activeIntegrationId={activeIntegrationId}
                    error={isError}
                    integration={excelIntegration}
                    key="excel"
                    onClick={handleConnectClick}
                    connectTextOverride={<FormattedMessage id="predict.upload_excel" defaultMessage="Upload Excel" />}
                  />
                )}
              </div>

              <DemoOrganisations
                activeIntegrationId={activeIntegrationId}
                handleConnectClick={handleConnectClick}
                demoIntegrations={demoIntegrations}
                error={isError}
              />
            </div>
          </div>
        )}
      </Form>
    </Sidebar>
  );
};
